import { useCallback, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router';
import type { Granularity, IncidentOrder, RoadUser } from '../../src/api/queries';
import { isCalendarDate } from '../../src/parse/dates';
import { canonicalList, ROAD_USERS } from '../../src/wire';
import { todayInTallinn } from './format';

/**
 * The URL is the view state (spec §7).
 *
 * Every filter a user picks lives in the query string and nowhere else: no context, no store, no
 * `useState` copy that a reload would drop. That single decision buys three things a dashboard is
 * judged on — a link that shows the reader what the sender was looking at, a refresh that lands
 * where you were, and a back button that undoes a filter change instead of leaving the page.
 *
 * Two rules keep it honest:
 *
 *  1. **Default-valued params are omitted.** The shared URL is `/vargused?g=week`, not forty
 *     characters of parameters that all say "as it comes". Encoding is therefore always relative to
 *     a `defaults` object, and a page that wants its own defaults must hand the same object to both
 *     halves of the round trip. No page overrides them today: every page passes
 *     {@link DEFAULT_VIEW_STATE}. The domestic-violence page is the near miss — it needs
 *     `domesticOnly` both off and on at once, one panel each — and it does that by deriving two
 *     states from this one rather than by defaulting the field, so `?domestic=` has no effect there
 *     at all.
 *  2. **Decoding never throws.** A query string is user input — hand-edited, truncated by a chat
 *     client, left over from an older version of this app. Anything unrecognised falls back to the
 *     default for that one field; the rest of the state survives.
 */

export interface ViewState {
  g: Granularity;
  from: string;
  to: string;
  /** A prefecture, the `UNSPECIFIED` sentinel ("Määramata"), or `null` for no filter. */
  prefecture: string | null;
  domesticOnly: boolean;
  /**
   * How the incident feed is sorted.
   *
   * It lives here rather than in a page's `useState` because it is a filter like any other: a reader
   * who sorts a theft feed by damage and sends the link is sending "the biggest thefts", and a
   * recipient who lands on "newest first" is looking at a different page than the one described to
   * them. `IncidentOrder` is the API's own union, and `queries.ts` tags its cursors with the
   * ordering that minted them, so this value is also part of the feed's cache key.
   */
  order: IncidentOrder;
  /** A county, the `UNSPECIFIED` sentinel ("Määramata"), or `null` — the traffic page's geography
   * (§7.5). The sentinel mostly means a road crossing several counties, not a missing place. */
  county: string | null;
  /** One of the four road-user chips (design §6), or `null` for the unfiltered page. */
  roadUser: RoadUser | null;
  /**
   * The municipality multi-select (D8) — EHAK labels and/or the `UNSPECIFIED` sentinel, or `[]` for
   * no filter. The only list-valued field here, so it is the only one with a canonical form: sorted
   * and deduped, because {@link nextSearch} compares encoded strings and a set re-picked in a
   * different order would otherwise push a history entry that changes nothing.
   */
  municipalities: readonly string[];
}

/** The first day the **digest** corpus covers, kept for the digest pages alone (R18) — the traffic
 * page derives its own left edge, three days later, where this would be wrong (§8.1). */
export const CORPUS_START = '2018-09-25';

const GRANULARITIES: readonly Granularity[] = ['day', 'week', 'month'];

const ORDERS: readonly IncidentOrder[] = ['date_desc', 'damage_desc'];

/**
 * The state a page shows before anyone touches a control: the whole corpus, by month.
 *
 * `to` is **today in Europe/Tallinn**, not in UTC. Every other date in this app is a Tallinn
 * calendar day — the corpus is Estonian police reports, `queries.ts` buckets by Tallinn days, and
 * the range presets already use {@link todayInTallinn}. Between midnight and 03:00 local time UTC
 * is still on yesterday, so a UTC `to` would quietly cut today's posts out of the default window
 * for three hours every night, and only for readers awake in them.
 *
 * It is resolved once at module load rather than per render, so the value stays stable for the life
 * of the tab — a `to` that silently advanced at midnight would change what "default" means
 * mid-session and make an omitted parameter mean two different windows.
 */
export const DEFAULT_VIEW_STATE: ViewState = Object.freeze({
  g: 'month',
  from: CORPUS_START,
  to: todayInTallinn(),
  prefecture: null,
  domesticOnly: false,
  order: 'date_desc',
  county: null,
  roadUser: null,
  municipalities: Object.freeze([]),
});

// ─── Encoding ───────────────────────────────────────────────────────────────

/**
 * The query string for a state, with everything that equals its default left out.
 *
 * `defaults` must be the same object the matching {@link decodeViewState} call is given — that
 * pairing is what makes an omitted parameter recoverable at all.
 */
export function encodeViewState(s: ViewState, defaults: ViewState = DEFAULT_VIEW_STATE): string {
  const parts: string[] = [];
  if (s.g !== defaults.g) parts.push(`g=${s.g}`);
  if (s.from !== defaults.from) parts.push(`from=${encodeURIComponent(s.from)}`);
  if (s.to !== defaults.to) parts.push(`to=${encodeURIComponent(s.to)}`);
  if (s.prefecture !== defaults.prefecture) {
    // An empty value is how "cleared" is spelled, which matters only when the page's own default
    // *is* a prefecture: `?prefecture=` then means "all of Estonia", not "unset".
    parts.push(`prefecture=${encodeURIComponent(s.prefecture ?? '')}`);
  }
  if (s.domesticOnly !== defaults.domesticOnly) parts.push(`domestic=${s.domesticOnly}`);
  if (s.order !== defaults.order) parts.push(`order=${s.order}`);
  // The same "cleared is a value" rule as `prefecture`, one column over.
  if (s.county !== defaults.county) parts.push(`county=${encodeURIComponent(s.county ?? '')}`);
  if (s.roadUser !== defaults.roadUser) parts.push(`road=${s.roadUser ?? ''}`);
  // Last, so every URL this app already emitted stays byte-identical. Compared in canonical form
  // rather than by identity: the field is an array, so a freshly decoded default would otherwise
  // put `municipality=` into every URL, and re-picking one set in another order a no-op history
  // entry — the rule below compares encoded strings.
  const list = canonicalList(s.municipalities).join(',');
  if (list !== canonicalList(defaults.municipalities).join(',')) {
    parts.push(`municipality=${encodeURIComponent(list)}`);
  }
  return parts.length === 0 ? '' : `?${parts.join('&')}`;
}

// ─── Decoding ───────────────────────────────────────────────────────────────

function granularityOf(raw: string | null): Granularity | null {
  return GRANULARITIES.find((g) => g === raw) ?? null;
}

/**
 * The feed ordering, validated against the API's union rather than passed through.
 *
 * `order` is the one field the server refuses loudly — `/api/incidents` 400s on an unknown value,
 * and a cursor minted under one ordering is rejected by the other — so an unrecognised parameter is
 * dropped here in favour of the default, which is the rule every other field in this file follows.
 */
function orderOf(raw: string | null): IncidentOrder | null {
  return ORDERS.find((o) => o === raw) ?? null;
}

/**
 * A window bound, or `null` when the string does not name a day the calendar has.
 *
 * {@link isCalendarDate} is *imported* from `src/parse/dates.ts` rather than reimplemented, because
 * the whole point of the check is to agree with the API: a bound the server would 400 (it would
 * otherwise wedge the bucket walk in `queries.ts`) buys only a failed request from here, so the
 * default window is the better answer for a typo. A local round trip through `new Date(...)` looks
 * equivalent and is not — it accepts the years 0001-0099, which the server's `ymd` rejects because
 * `addDays('0032-01-01', 1)` would answer `1932-01-02`. Two validators that disagree let the UI
 * carry exactly the bound this function exists to keep out of the URL.
 *
 * The import is safe to bundle: `dates.ts` has no imports at all and touches nothing but `Intl`.
 */
function dateOf(raw: string | null): string | null {
  return raw !== null && isCalendarDate(raw) ? raw : null;
}

/**
 * A prefecture is validated as a non-empty string, not against a fixed list.
 *
 * `/api/meta` publishes the prefectures actually present in the database and the UI builds its
 * dropdown from that; a hardcoded list here would be a second copy, free to drift the day a post's
 * heading changes. A nonsense value costs one request that matches no rows — `routes.ts` makes the
 * same trade for the same reason.
 */
function prefectureOf(raw: string | null, fallback: string | null): string | null {
  if (raw === null) return fallback;
  const trimmed = raw.trim();
  return trimmed === '' ? null : trimmed;
}

/** The road user, validated against the API's four values: `/api/series` 400s on anything else, so
 * an unrecognised chip is dropped here rather than turned into a failed request. */
function roadUserOf(raw: string | null, fallback: RoadUser | null): RoadUser | null {
  if (raw === null) return fallback;
  return ROAD_USERS.find((value) => value === raw) ?? fallback;
}

/**
 * The municipality list, parsed and canonicalised but **not** validated (R14).
 *
 * There is no list here to validate against — the options are data, and the page's own list arrives
 * with `/api/meta` a fetch later. A value dropped for being unknown would be a shared link losing
 * its filter for one render and then re-encoding the shorter URL over it; instead the value is kept
 * and `municipalityOptions` re-adds it to the control, so it is visible and clearable.
 */
function listOf(raw: string | null, fallback: readonly string[]): string[] {
  return raw === null ? [...fallback] : canonicalList(raw.split(','));
}

function boolOf(raw: string | null): boolean | null {
  if (raw === 'true' || raw === '1') return true;
  if (raw === 'false' || raw === '0') return false;
  return null;
}

/**
 * The window, with an inverted pair dropped rather than repaired.
 *
 * `?from=2026-08-01&to=2026-07-01` names no days, and `routes.ts` now refuses it outright — so
 * carrying it into state buys a failed request and a page of error cards. Of the two repairs
 * available, falling back is the smaller surprise. Swapping the bounds would render a window the
 * reader never asked for, and because the URL *is* the view state, the next `encodeViewState` would
 * write that different window back over the link they were handed — a shared link that quietly
 * becomes a different link on arrival.
 *
 * **Both** bounds are dropped, not the offending one. This is a pair: keeping the half that happens
 * to sort correctly would leave a window with one end the reader chose and one they did not, which
 * is the same silent substitution in a smaller costume. Dropping the pair is rule 2 at the top of
 * this file applied to the one field that is really two.
 *
 * Lexicographic comparison is exact: both values have already passed {@link isCalendarDate}, and
 * `YYYY-MM-DD` sorts as its dates do.
 */
function windowOf(params: URLSearchParams, defaults: ViewState): { from: string; to: string } {
  const from = dateOf(params.get('from')) ?? defaults.from;
  const to = dateOf(params.get('to')) ?? defaults.to;
  return from <= to ? { from, to } : { from: defaults.from, to: defaults.to };
}

/** A query string (with or without its `?`) read back into a state. Never throws. */
export function decodeViewState(search: string, defaults: ViewState): ViewState {
  const params = new URLSearchParams(search);
  return {
    g: granularityOf(params.get('g')) ?? defaults.g,
    ...windowOf(params, defaults),
    prefecture: prefectureOf(params.get('prefecture'), defaults.prefecture),
    domesticOnly: boolOf(params.get('domestic')) ?? defaults.domesticOnly,
    order: orderOf(params.get('order')) ?? defaults.order,
    // A county is validated as a non-empty string for `prefectureOf`'s reason: the option list is
    // data (`/api/traffic/facets`), and a second copy here would be free to drift from it.
    county: prefectureOf(params.get('county'), defaults.county),
    roadUser: roadUserOf(params.get('road'), defaults.roadUser),
    municipalities: listOf(params.get('municipality'), defaults.municipalities),
  };
}

// ─── The hook ───────────────────────────────────────────────────────────────

/**
 * The search string a patch should navigate to, or `null` when that is where we already are.
 *
 * **A patch that changes nothing is not a navigation.** Re-selecting the granularity already shown,
 * or clicking the range preset already applied, would otherwise push a history entry identical to
 * the current one — and Back would then appear broken, doing nothing once per redundant click
 * before finally leaving the page. Those clicks are not hypothetical: a segmented control gives no
 * feedback for pressing the option that is already active, so it invites exactly that.
 *
 * The comparison is on the *encoded* string rather than on the two states, which also absorbs a
 * patch that rewrites a field to the value it already held, and a URL whose parameters are in a
 * different order or carry a value equal to its default.
 *
 * Split out of {@link useUrlState} so the rule is testable. The hook itself is `useLocation` /
 * `useNavigate` glue, and this repo has no DOM to click through; whether a click is a navigation is
 * pure, and is the half worth pinning.
 */
export function nextSearch(
  search: string,
  defaults: ViewState,
  changes: Partial<ViewState>,
): string | null {
  const next = encodeViewState({ ...decodeViewState(search, defaults), ...changes }, defaults);
  // `useLocation().search` carries its `?` and is `''` when there are no parameters — the same two
  // spellings `encodeViewState` produces. Normalised anyway, because `decodeViewState` documents
  // that it accepts a search string with or without one, and a caller may pass either.
  const current = search === '' || search.startsWith('?') ? search : `?${search}`;
  return next === current ? null : next;
}

/**
 * The current view state and a patcher that writes the next one into the URL.
 *
 * **Changing a filter pushes a history entry rather than replacing one.** Picking a prefecture is a
 * navigation in the reader's mind — the thing they expect Back to undo — and replacing would send
 * them off the page instead, which is the more expensive mistake to make. The patcher re-decodes
 * the live search string before merging, so two patches fired from the same render (a date picker
 * that sets `from` and `to` in two calls) cannot lose each other's writes.
 *
 * Pass a stable `defaults` — a module constant, or one wrapped in `useMemo`. A fresh object literal
 * every render only costs a re-decode, but it also churns the patcher's identity.
 */
export function useUrlState(
  defaults: ViewState = DEFAULT_VIEW_STATE,
): [ViewState, (patch: Partial<ViewState>) => void] {
  const { search } = useLocation();
  const navigate = useNavigate();

  const state = useMemo(() => decodeViewState(search, defaults), [search, defaults]);

  const patch = useCallback(
    (changes: Partial<ViewState>) => {
      const next = nextSearch(search, defaults, changes);
      // Only the search half is given, so the current pathname is kept.
      if (next !== null) navigate({ search: next });
    },
    [search, defaults, navigate],
  );

  return [state, patch];
}
